import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import AgGridWrapperTracker from '@/app/common/components/ag-grid/AgGridWrapperTracker';
import { useRef, useEffect } from 'react';
import { Button, Col, Form, Row } from 'react-bootstrap';
import type { AgGridReact } from 'ag-grid-react';
import { useLocation, useOutletContext, useSearchParams } from 'react-router-dom';
import type { OutletContext } from './pageTypes';
import type { GridRef } from '../components/keywords/drawers/types';import { useGetUserInfoQuery } from '@/app/common/api/authApi';
import keywordsApi, {
  useDeleteGroupsMutation,
  useEditGroupMutation,
  useGetGroupsFuncMutation,
  useGetGroupsQuery,
  useGetTariffInfoQuery,
} from '@/app/KeywordTracker/api/keywordsApi';
import CancelModal from '@/app/common/components/CancelModal';
import { errorMessage, successMessage } from '@/utils/toast';
import { useDispatch, useSelector } from 'react-redux';
import AddGroupsDrawer from '../components/keywords/drawers/AddGroupsDrawer';
import AgGridSkeleton from '@/app/common/components/skeleton/AgGridSkeleton';
import { groupsColumnConfig } from '@/app/KeywordTracker/config/groupsColumnConfig';
import FiltersComponent from '@/app/common/components/filters/FiltersComponent';
import IconAlert from '@/components/common/IconAlert';
import Flex from '@/components/common/Flex';
import ChildrensTooltip from '@/components/common/ChildrensTooltip';
import TextLoading from '../../common/components/skeleton/TextLoading';
import TimePicker from '../components/common/TimePicker';
import FiltersComponentSkeleton from '../../common/components/filters/FiltersComponentSkeleton';
import { AiOutlineFolder } from 'react-icons/ai';
import useDomain from '@/hooks/useDomain';
import { breakpointsMediaQuery, useMediaQuery } from '@/helpers/utils';
import { deleteTransaction } from '@/app/common/components/ag-grid/deleteTransaction';
import { hideModal, showModal } from '@/app/common/reducers/simpleModal';
import {
  isTrackerKeywordsTariffZero,
  TRACKER_KEYWORDS_INACTIVE_TOOLTIP
} from '@/app/KeywordTracker/utils/trackerTariff';
import TrackerKeywordsInactiveAlert from '../components/TrackerKeywordsInactiveAlert';
import DataLoadError from '@/components/errors/DataLoadError';
import {
  shouldShowDataLoadError,
  isNetworkApiError
} from '@/app/common/utils/apiErrorUtils';

const Groups = () => {
  const dispatch = useDispatch();
  const isMdUp = useMediaQuery(breakpointsMediaQuery.md);
  const { domain_slug, domain } = useDomain();
  const { data: userInfo } = useGetUserInfoQuery();
  const [, setBreadcrumbData] = useOutletContext<OutletContext>();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    setBreadcrumbData([{ text: 'دسته ها', href: '#' }]);
  }, [setBreadcrumbData]);

  const { pathname } = useLocation();

  useEffect(() => {
    dispatch(keywordsApi.util.invalidateTags(['groups']));
  }, [dispatch, pathname]);

  const gridRef = useRef<AgGridReact | null>(null);
  const { data, isLoading, isFetching, isError, error, refetch } =
    useGetGroupsQuery({
      from_date: Number(searchParams.get('from_date')),
      to_date: Number(searchParams.get('to_date')),
      domain_slug
    });
  const [getGroupFunc] = useGetGroupsFuncMutation();
  const [editGroup] = useEditGroupMutation();
  const { data: trackerInfo, isFetching: isFetchingTrackerInfo } =
    useGetTariffInfoQuery(domain_slug);
  const isTrackerAddonInactive = () =>
    isTrackerKeywordsTariffZero(trackerInfo);
  const isGroupsOver = () =>
    trackerInfo?.data?.used?.num_of_groups >
    trackerInfo?.data?.tariff?.num_of_groups;
  const isAccessAllowed = () =>
    trackerInfo?.data?.access_level !== 3 && !isFetchingTrackerInfo;

  const getAddButtonTooltip = () => {
    if (isTrackerAddonInactive()) {
      return TRACKER_KEYWORDS_INACTIVE_TOOLTIP;
    }
    if (isGroupsOver()) {
      return 'تعداد دسته ها با طرح خریداری شده مطابقت ندارد.';
    }
    if (!isAccessAllowed()) {
      return 'شما دسترسی لازم را ندارید';
    }
    return '';
  };

  return (
    <>
      {!isFetchingTrackerInfo && isTrackerAddonInactive() && (
        <div className="my-3">
          <TrackerKeywordsInactiveAlert />
        </div>
      )}
      <Row className="g-3 g-md-0 my-3 p-0 justify-content-between">
        <Col xs={12} sm="auto">
          <Flex alignItems="center" className="gap-2 w-100">
            <ChildrensTooltip
              className="w-100"
              placement="top"
              html={getAddButtonTooltip()}
            >
              <Button
                onClick={() => dispatch(showModal('ADD_GROUP'))}
                variant="falcon-primary"
                size="sm"
                disabled={
                  isTrackerAddonInactive() ||
                  isGroupsOver() ||
                  !isAccessAllowed()
                }
                className="d-flex w-100 py-1 gap-2 justify-content-center align-items-center"
              >
                {isTrackerAddonInactive() ? (
                  <FontAwesomeIcon icon="lock" className="fs--1" />
                ) : (
                  <AiOutlineFolder className="fs-1" />
                )}
                افزودن دسته
                {!isTrackerAddonInactive() && isGroupsOver() && (
                  <FontAwesomeIcon icon="lock" className="fs--1" />
                )}
              </Button>
            </ChildrensTooltip>
          </Flex>
        </Col>

        <Col xs={12} sm="auto" className="mb-3 mb-md-0">
          <Row className="g-2 gy-md-0 h-100 align-items-center">
            <Col xs="auto">
              <h6 className="text-700 mb-0 text-nowrap">بازه زمانی</h6>
            </Col>
            <Col xs={12} md="auto">
              {!isFetching && !isLoading ? (
                <TimePicker
                  initDay={data?.data?.from_date}
                  lastDay={data?.data?.to_date}
                />
              ) : (
                <TextLoading
                  width={isMdUp ? 205 : 100}
                  height={24}
                  isWidthPx={isMdUp}
                />
              )}
            </Col>
          </Row>
        </Col>
      </Row>
      <div>
        {shouldShowDataLoadError(isError, error) ? (
          <DataLoadError
            error={error}
            onRetry={() => refetch()}
            isRetrying={isFetching}
            isNetworkError={isNetworkApiError(error)}
          />
        ) : !isFetching && !isLoading ? (
          <>
            {/* FOR BUILD NOW */}
            <FiltersComponent
              preQueries={
                searchParams.get('from_date') || searchParams.get('to_date')
                  ? `from_date=${searchParams.get('from_date')}&to_date=${searchParams.get('to_date')}`
                  : ''
              }
              columns={groupsColumnConfig(
                domain_slug,
                domain,
                editGroup,
                userInfo?.data?.pathName,
                gridRef
              )}
            />
            {isGroupsOver() && (
              //  ICON ALERT FOR SHOWING OVER ACCOUNT TARIFF
              <IconAlert variant="warning">
                <Flex direction="column" className="gap-1">
                  {isGroupsOver() && (
                    <div>
                      درحال حاضر
                      <strong className="mx-2">
                        {trackerInfo?.data?.used?.num_of_groups}
                      </strong>
                      دسته داری و با توجه به طرح انتخابی باید تعدادش رو به
                      <strong className="mx-2">
                        {trackerInfo?.data?.tariff?.num_of_groups}
                      </strong>
                      برسونی (
                      <strong className="mx-2">
                        {trackerInfo?.data?.used?.num_of_groups -
                          trackerInfo?.data?.tariff?.num_of_groups}
                      </strong>
                      دسته باید حذف بشه)
                    </div>
                  )}
                </Flex>
              </IconAlert>
            )}
            <AgGridWrapperTracker
              ref={gridRef}
              settings={{
                pagination: true,
                fullscreen: true,
                changeHeight: true,
                //   resetGrid: true,
                saveGrid: false,
                sideBar: true,
                // columnsState: 'tableSettings?.data',
                export: {
                  enabled: false
                  // fetchFunction: 'exportTable'
                }
              }}
              perPage={20}
              otherButtons={
                <div className="d-flex gap-1 align-items-center">
                  <GridButtons isAccessAllowed={isAccessAllowed} />
                </div>
              }
              height="746px"
              data={data?.data?.rows}
              total={data?.data?.total}
              fetchFunction={getGroupFunc}
              additionalData={{
                domain_slug,
                from_date: Number(searchParams.get('from_date')),
                to_date: Number(searchParams.get('to_date'))
              }}
              columns={groupsColumnConfig(
                domain_slug,
                domain,
                editGroup,
                userInfo?.data?.pathName,
                gridRef
              )}
            />
            <DeleteModalWrapper gridRef={gridRef} />
            <AddGroupsDrawer gridRef={gridRef} />
          </>
        ) : (
          <>
            {/* FOR BUILD NOW */}
            <Col xxl={12} className="mb-2">
              <FiltersComponentSkeleton />
            </Col>
            <AgGridSkeleton
              height="696px"
              settings={{
                pagination: true,
                fullscreen: true,
                changeHeight: true,
                resetGrid: false,
                saveGrid: false,
                exportBtn: false
              }}
            />
          </>
        )}
      </div>
    </>
  );
};

export default Groups;

interface DeleteModalWrapperProps {
  gridRef: GridRef;
}

const DeleteModalWrapper = ({ gridRef }: DeleteModalWrapperProps) => {  const selectedKeys = useSelector(state => state.selectedKeys);
  const { domain_slug } = useDomain();
  const [deleteGroups] = useDeleteGroupsMutation();
  const modalKey = useSelector(state => state.simpleModal);
  const dispatch = useDispatch();
  return (
    <>
      <CancelModal
        desc={
          <Form.Group>
            <Form.Label>
              آیا از حذف {selectedKeys.length} دسته زیر مطمئن هستی؟
            </Form.Label>
            <Form.Control
              // readOnly
              disabled
              rows={selectedKeys.length > 3 ? 3 : selectedKeys.length}
              as="textarea"
              value={selectedKeys.map(({ label }) => `${label}`).join('\n')}
            />
            <div className="d-flex mt-2 align-items-center gap-2">
              <FontAwesomeIcon
                icon="exclamation-circle"
                className="text-warning"
              />
              پس از حذف دسته ها کلمات دیگر این دسته ها رو نخواهند داشت.
            </div>
          </Form.Group>
        }
        show={modalKey === 'DELETE_GROUP'}
        setShow={() => dispatch(hideModal())}
        title={'حذف دسته'}
        submitFunc={() => {
          deleteGroups({
            domain_slug,
            group_ids: selectedKeys.map(key => key.id)
          })
            .unwrap()
            .then(res => {
              deleteTransaction(res?.data, gridRef);
              successMessage('حذف دسته ها با موفقیت انجام شد.');
            })
            .catch(err => {
              errorMessage('خطایی در حذف دسته ها وجود داره');
              console.error(err);
            })
            .finally(() => {
              dispatch(hideModal());
            });
        }}
        submitTitle="آره، حذف بشه"
      />
    </>
  );
};
interface GridButtonsProps {
  isAccessAllowed: () => boolean;
}

const GridButtons = ({ isAccessAllowed }: GridButtonsProps) => {
  const dispatch = useDispatch();
  const selectedKeys = useSelector(state => state.selectedKeys);
  return (
    selectedKeys.length > 0 && (
      <ChildrensTooltip
        html={isAccessAllowed() ? '' : 'شما دسترسی لازم را ندارید'}
      >
        <Button
          style={{
            height: 30,
            padding: 12
          }}
          disabled={!isAccessAllowed()}
          onClick={() => dispatch(showModal('DELETE_GROUP'))}
          variant="danger"
          size="sm"
          className="d-flex gap-2 align-items-center"
        >
          <FontAwesomeIcon icon="trash-alt" className="fs-0" />
        </Button>
      </ChildrensTooltip>
    )
  );
};
